import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { LayoutItem, NumberLineItem, WidgetJSON } from '../../../../transformers/zod-schemas';
import type { WidgetValidation } from '../../../../validators';
import type { UserAnswerValue } from '../../../types';
import { toPercent } from '../../../utils/number-line.utils';
import { LayoutRenderer } from '../../LayoutRenderer';

type ArrowItem = NonNullable<NumberLineItem['arrows']>[number];

const ARROW_ARC_LEVEL_HEIGHT  = 80;
const ARROW_ARC_VIEWBOX_HEIGHT = 64;
/** SVG fallback width (px) used before the element is measured. */
const ARROW_ARC_VIEWBOX_WIDTH  = 100;
/** Y coordinate of the arc base (= arrowhead tip) in SVG user units. */
const ARROW_ARC_BASE_Y         = 56;
const ARROW_ARC_MIN_LIFT       = 28;
const ARROW_ARC_MAX_LIFT       = 58;
const ARROW_ARC_LIFT_RATIO     = 1.15;
const ARROW_ARC_LABEL_OFFSET   = 24;
/** Arrowhead dimensions are fixed — no need to scale per pixel width. */
const ARROW_ARC_HEAD_LENGTH     = 8;
const ARROW_ARC_HEAD_HALF_WIDTH = 3.2;

export interface ArrowArcLayout {
  arrow: ArrowItem;
  leftPct: number;
  rightPct: number;
  widthPct: number;
  level: number;
}

interface IndexedArrowArcLayout extends ArrowArcLayout {
  index: number;
}

function getArrowArcLift(widthPct: number): number {
  return Math.min(ARROW_ARC_MAX_LIFT, Math.max(ARROW_ARC_MIN_LIFT, widthPct * ARROW_ARC_LIFT_RATIO));
}

function fmt(n: number): number {
  return Number(n.toFixed(2));
}

function getArrowArcSvgWidth(renderedWidthPx: number): number {
  return renderedWidthPx > 0 ? renderedWidthPx : ARROW_ARC_VIEWBOX_WIDTH;
}

function getArrowArcLabelTop(widthPct: number): number {
  const svgTop = ARROW_ARC_LEVEL_HEIGHT - ARROW_ARC_VIEWBOX_HEIGHT;
  const arcTop = svgTop + ARROW_ARC_BASE_Y - getArrowArcLift(widthPct);
  return Math.max(0, arcTop - ARROW_ARC_LABEL_OFFSET);
}

/**
 * Returns both the arc shaft path and the arrowhead path in a single
 * geometry pass. The control-point inset scales with the SVG width so
 * the curve looks consistent regardless of the arc span.
 */
export function getArrowArcPaths(
  widthPct: number,
  svgWidthPx = ARROW_ARC_VIEWBOX_WIDTH,
): { arcPath: string; headPath: string } {
  const endX       = getArrowArcSvgWidth(svgWidthPx);
  const controlInset = Math.min(svgWidthPx * 0.18, 48);
  const lift       = getArrowArcLift(widthPct);
  const controlY   = fmt(ARROW_ARC_BASE_Y - lift);

  // Bezier tangent at the endpoint — used to align the arrowhead.
  const tangentAngle = Math.atan2(ARROW_ARC_BASE_Y - controlY, endX - (endX - controlInset));
  const tx = Math.cos(tangentAngle);
  const ty = Math.sin(tangentAngle);

  // Shaft ends just before the arrowhead tip.
  const shaftEndX   = fmt(endX  - tx * ARROW_ARC_HEAD_LENGTH);
  const shaftEndY   = fmt(ARROW_ARC_BASE_Y - ty * ARROW_ARC_HEAD_LENGTH);
  const endControlX = fmt(shaftEndX - tx * controlInset);
  const endControlY = fmt(shaftEndY - ty * controlInset);

  const arcPath = `M 0 ${ARROW_ARC_BASE_Y} C ${fmt(controlInset)} ${controlY} ${endControlX} ${endControlY} ${shaftEndX} ${shaftEndY}`;

  // Arrowhead triangle.
  const nx = -ty;
  const ny =  tx;
  const baseX = endX  - tx * ARROW_ARC_HEAD_LENGTH;
  const baseY = ARROW_ARC_BASE_Y - ty * ARROW_ARC_HEAD_LENGTH;
  const headPath = `M ${fmt(endX)} ${ARROW_ARC_BASE_Y} L ${fmt(baseX + nx * ARROW_ARC_HEAD_HALF_WIDTH)} ${fmt(baseY + ny * ARROW_ARC_HEAD_HALF_WIDTH)} L ${fmt(baseX - nx * ARROW_ARC_HEAD_HALF_WIDTH)} ${fmt(baseY - ny * ARROW_ARC_HEAD_HALF_WIDTH)} Z`;

  return { arcPath, headPath };
}

export function getArrowArcLayouts(
  arrows: NumberLineItem['arrows'],
  min: number,
  max: number,
): ArrowArcLayout[] {
  if (!arrows?.length) return [];

  const sorted = arrows
    .map((arrow, index): IndexedArrowArcLayout => {
      const fromPct = toPercent(arrow.from, min, max);
      const toPct = toPercent(arrow.to, min, max);

      return {
        arrow,
        index,
        leftPct: fromPct,
        rightPct: toPct,
        widthPct: toPct - fromPct,
        level: 0,
      };
    })
    .filter(layout => layout.widthPct > 0)
    .sort((a, b) => a.leftPct - b.leftPct || a.rightPct - b.rightPct || a.index - b.index);

  const levelRightEdges: number[] = [];
  for (const layout of sorted) {
    const reusableLevel = levelRightEdges.findIndex(rightPct => layout.leftPct >= rightPct);
    const level = reusableLevel === -1 ? levelRightEdges.length : reusableLevel;
    layout.level = level;
    levelRightEdges[level] = layout.rightPct;
  }

  return sorted.sort((a, b) => a.index - b.index);
}

function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateWidth = () => {
      const nextWidth = element.getBoundingClientRect().width;
      setWidth(previousWidth => previousWidth === nextWidth ? previousWidth : nextWidth);
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return [ref, width] as const;
}

export interface ArrowArcsRowProps {
  arrows: NumberLineItem['arrows'];
  min: number;
  max: number;
  widgets: Record<string, WidgetJSON>;
  values: Record<string, UserAnswerValue>;
  droppedInputs: Set<string>;
  validationDetails?: Record<string, WidgetValidation> | null;
  onChange: (widgetId: string, value: UserAnswerValue, fromDrop?: boolean, sourceOptionId?: string) => void;
  onInputFocus?: (widgetId: string) => void;
}

interface ArrowArcProps extends Omit<ArrowArcsRowProps, 'arrows' | 'min' | 'max'> {
  layout: ArrowArcLayout;
}

const ArrowArc: React.FC<ArrowArcProps> = ({
  layout: { arrow, leftPct, widthPct, level },
  widgets,
  values,
  droppedInputs,
  validationDetails,
  onChange,
  onInputFocus,
}) => {
  const [arcRef, renderedWidthPx] = useElementWidth<HTMLDivElement>();
  const hasLabel = arrow.content.length > 0;
  const svgWidthPx = getArrowArcSvgWidth(renderedWidthPx);
  const { arcPath, headPath } = useMemo(
    () => getArrowArcPaths(widthPct, svgWidthPx),
    [widthPct, svgWidthPx],
  );

  return (
    <div
      ref={arcRef}
      className="number-line-arrow-arc"
      data-level={level}
      style={{
        left: `${leftPct}%`,
        width: `${widthPct}%`,
        bottom: `${level * ARROW_ARC_LEVEL_HEIGHT}px`,
        // DSL color overrides the CSS default — only set when the author
        // explicitly wrote e.g. ARROW[from=0,to=5,color="#2B6DEA"]
        ...(arrow.color !== undefined && { '--nl-arrow-arc-color': arrow.color } as React.CSSProperties),
      }}
    >
      <svg
        className="number-line-arrow-arc-svg"
        viewBox={`0 0 ${svgWidthPx} ${ARROW_ARC_VIEWBOX_HEIGHT}`}
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      >
        <path
          className="number-line-arrow-arc-path"
          d={arcPath}
        />
        <path
          className="number-line-arrow-arc-head"
          d={headPath}
        />
      </svg>

      {hasLabel && (
        <div
          className="number-line-arrow-arc-label"
          style={{ top: `${getArrowArcLabelTop(widthPct)}px` }}
        >
          {arrow.content.map((subItem: LayoutItem) => (
            <LayoutRenderer
              key={subItem.id}
              item={subItem}
              widgets={widgets}
              values={values}
              droppedInputs={droppedInputs}
              validationDetails={validationDetails}
              onChange={onChange}
              onInputFocus={onInputFocus}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const ArrowArcsRow: React.FC<ArrowArcsRowProps> = ({
  arrows,
  min,
  max,
  widgets,
  values,
  droppedInputs,
  validationDetails,
  onChange,
  onInputFocus,
}) => {
  const layouts = useMemo(() => getArrowArcLayouts(arrows, min, max), [arrows, min, max]);

  if (layouts.length === 0) return null;

  const levelCount = Math.max(...layouts.map(layout => layout.level)) + 1;

  return (
    <div
      className="number-line-arrow-arcs-row"
      style={{ height: `${levelCount * ARROW_ARC_LEVEL_HEIGHT}px` }}
    >
      {layouts.map(layout => (
        <ArrowArc
          key={layout.arrow.id}
          layout={layout}
          widgets={widgets}
          values={values}
          droppedInputs={droppedInputs}
          validationDetails={validationDetails}
          onChange={onChange}
          onInputFocus={onInputFocus}
        />
      ))}
    </div>
  );
};
